import type { W_Job_FindDataForWorkOrderForm_VM, W_Job_FindDataForWorkOrderForm_VM_Attachment, W_Job_FindDataForWorkOrderForm_VM_Directory } from 'ab-api/web/job/findDataForWorkOrderForm';

import type WorkOrderUpsertVM from 'ab-viewModels/workOrder/workOrderUpsert.viewModel';
import type { AddressWrapperVM, WorkLocationAddressVM, AddressBackupVM } from 'ab-viewModels/workOrder/workOrderUpsert.viewModel';
import type JobUpsertVM from 'ab-viewModels/workRequest/jobUpsert.viewModel';

import type WorkOrderRM from 'ab-dtos/requestModels/workOrder/upsert';
import type { WorkOrderResourceLookupRM } from 'ab-dtos/requestModels/workOrder/upsert';

import { filterMap, getId } from '@acceligentllc/shared/utils/array';
import * as TimeUtils from '@acceligentllc/shared/utils/time';
import * as TimeOptionUtils from '@acceligentllc/shared/utils/timeOption';

import type { CDLStatus } from '@acceligentllc/shared/enums/cdlStatus';
import type { ColorPalette } from '@acceligentllc/shared/enums/color';
import type { DirectoryIcon } from '@acceligentllc/shared/enums/directoryIcon';
import type { FileType } from '@acceligentllc/shared/enums/fileType';
import { FormFieldArrayStatus } from '@acceligentllc/shared/enums/formFieldArrayStatus';
import type { ResourceStatus } from '@acceligentllc/shared/enums/resourceStatus';
import { TimeFormat } from '@acceligentllc/shared/enums/timeFormat';
import { WorkOrderPosition } from '@acceligentllc/shared/enums/workOrderPosition';
import type { WorkOrderStatus } from '@acceligentllc/shared/enums/workOrderStatus';
import type { WorkRequestStatus } from '@acceligentllc/shared/enums/workRequestStatus';

interface ShiftFM {
	id: number;
	name: string;
}

class AddressBackupFM {
	aa1: Nullable<string>;
	aa2: Nullable<string>;
	aa3: Nullable<string>;
	country: Nullable<string>;
	latitude: Nullable<number>;
	longitude: Nullable<number>;
	locality: Nullable<string>;
	postalCode: Nullable<string>;
	street: Nullable<string>;
	streetNumber: Nullable<string>;
	suite: Nullable<string>;
	postalOfficeBoxCode: Nullable<string>;
	neighborhood: Nullable<string>;
	formattedAddress: Nullable<string>;

	constructor(backup: AddressBackupVM) {
		this.latitude = backup.latitude;
		this.longitude = backup.longitude;
		this.aa1 = null;
		this.aa2 = null;
		this.aa3 = null;
		this.country = null;
		this.locality = null;
		this.postalCode = null;
		this.street = null;
		this.streetNumber = null;
		this.suite = null;
		this.postalOfficeBoxCode = null;
		this.neighborhood = null;
		this.formattedAddress = null;
	}
}

export class AddressFM {
	id?: number;
	city: Nullable<string>;
	neighborhood: Nullable<string>;
	street: Nullable<string>;
	state: Nullable<string>;
	country: Nullable<string>;
	streetNumber: Nullable<string>;
	postalCode: Nullable<string>;
	notes: Nullable<string>;
	location: Nullable<string>;
	backup: Nullable<AddressBackupFM>;

	constructor(address: WorkLocationAddressVM) {
		this.id = address.id;
		this.city = address.city;
		this.neighborhood = address.neighborhood;
		this.location = AddressFM._formatLocation(address);
		this.state = address.state;
		this.country = address.country;
		this.streetNumber = address.streetNumber;
		this.street = address.street;
		this.postalCode = address.postalCode;
		this.notes = address.notes;
		this.backup = address.backup ? new AddressBackupFM(address.backup) : null;
	}

	private static _formatLocation(address: WorkLocationAddressVM) {
		const location: string[] = [];
		if (address.street) {
			location.push(address.street);
		}
		if (address.neighborhood) {
			location.push(address.neighborhood);
		}
		if (address.city) {
			location.push(address.city);
		}
		return `${address.streetNumber ? address.streetNumber : ''} ${location.join(', ')}`;
	}
}

export class AddressArrayFM {
	address: AddressFM;
	status: FormFieldArrayStatus;

	constructor(address: AddressWrapperVM) {
		this.address = new AddressFM(address.address);
		this.status = FormFieldArrayStatus.OLD;
	}

	private static _constructorMap(address: AddressWrapperVM) {
		return new AddressArrayFM(address);
	}

	public static bulkConstructor(addresses: AddressWrapperVM[]) {
		return addresses.map(AddressArrayFM._constructorMap);
	}

	public static bulkToRequestModel(addresses: AddressArrayFM[]) {
		return filterMap(
			addresses,
			(_addr) => !!_addr?.status,
			(_addr) => _addr
		);
	}
}

interface ContactFM {
	id: number;
	role: Nullable<string>;
	contactAddressIds: number[];
	contactEmailIds: number[];
	contactPhoneIds: number[];
}

interface SkillFM {
	id: number;
}

interface EmployeeFM {
	id: number;
	uniqueId: string;
	firstName: string;
	lastName: string;
	fullName: string;
	skills: SkillFM[];
	locationId: Nullable<number>;
	locationNickname: Nullable<string>;
	locationColor: Nullable<ColorPalette>;
	cdlStatus: CDLStatus;
}

interface EquipmentFM {
	id: number;
	code: string;
	specification: Nullable<string>;
	equipmentCostId: number;
	equipmentCost?: Nullable<EquipmentCostFM>;
	equipmentCostCategoryColor: Nullable<ColorPalette>;
	skills: SkillFM[];
	allSkills: SkillFM[];
	showOnScheduleBoard: boolean;
}

interface WageRateFM {
	id: number;
	type: string;
	wageClassification: string;
}

interface EquipmentCostFM {
	id: number;
	categoryName: string;
	skills: SkillFM[];
	categoryColor: Nullable<ColorPalette>;
}

interface TemporaryEmployeeFM {
	id: number;
	firstName: string;
	lastName: string;
	fullName: string;
	uniqueId: string;
}

class WorkOrderResourceLookupFM {
	id: number;
	workOrderId: number;
	workOrderEmployeeId?: Nullable<number>;
	workOrderEquipmentId?: Nullable<number>;
	workOrderPlaceholderId?: Nullable<number>;
	workOrderTemporaryEmployeeId?: Nullable<number>;
	employeeId?: Nullable<number>;
	employee?: Nullable<EmployeeFM>;
	equipmentId?: Nullable<number>;
	equipment?: Nullable<EquipmentFM>;
	wageRateId?: Nullable<number>;
	wageRate?: Nullable<WageRateFM>;
	equipmentCostId?: Nullable<number>;
	equipmentCost?: Nullable<EquipmentCostFM>;
	temporaryEmployeeId?: Nullable<number>;
	temporaryEmployee?: Nullable<TemporaryEmployeeFM>;
	skills?: SkillFM[];
	index: number;
	perDiem: Nullable<boolean>;
	perDiemAmount: Nullable<string>;
	isCrewLead: boolean;

	public static toRequestModel(form: WorkOrderResourceLookupFM): WorkOrderResourceLookupRM {
		return {
			id: form.id,
			workOrderId: form.workOrderId,
			workOrderEmployeeId: form.workOrderEmployeeId,
			workOrderEquipmentId: form.workOrderEquipmentId,
			workOrderPlaceholderId: form.workOrderPlaceholderId,
			workOrderTemporaryEmployeeId: form.workOrderTemporaryEmployeeId,
			employeeId: form.employeeId,
			employee: form.employee,
			equipmentId: form.equipmentId,
			equipment: form.equipment,
			wageRateId: form.wageRateId ?? null,
			wageRate: form.wageRate ?? null,
			equipmentCostId: form.equipmentCostId ?? null,
			equipmentCost: form.equipmentCost ?? null,
			temporaryEmployeeId: form.temporaryEmployeeId,
			temporaryEmployee: form.temporaryEmployee,
			skills: form.skills,
			index: form.index,
			perDiem: form.perDiem,
			perDiemAmount: form.perDiemAmount,
			isCrewLead: form.isCrewLead,
		};
	}

	public static bulkToRequestModel(resourceLookups: WorkOrderResourceLookupFM[]) {
		return resourceLookups.map(WorkOrderResourceLookupFM.toRequestModel);
	}
}

interface UserFM {
	id: number;
	firstName: string;
	lastName: string;
	uniqueId: string;
}

interface UpdatedByFM {
	/** userId */
	id: number;
	fullName: string;
}

export class JobFM {
	id: number;
	status: WorkRequestStatus;
	isInternal: boolean;
	customerCompany: Nullable<string>;
	customerFormatted: Nullable<string>;
	customerFullName: Nullable<string>;
	office: Nullable<string>;
	title: Nullable<string>;
	projectManagerId?: Nullable<number>;
	projectManager?: Nullable<UserFM>;
	jobCode: string;
	travelLocationShort: Nullable<string>;
	deliverableSoftware: Nullable<string>;
	deliverableFileFormat: Nullable<string>;
	deliverableCodeStandard: Nullable<string>;
	isDeliverable: boolean;

	static toWorkOrderJobRequestModel(vm: W_Job_FindDataForWorkOrderForm_VM): JobFM {
		return {
			id: vm.id,
			jobCode: vm.jobCode,
			isInternal: vm.isInternal,
			customerCompany: vm.customerCompany,
			customerFormatted: vm.customerFormatted,
			customerFullName: vm.customerFullName,
			status: vm.status,
			travelLocationShort: vm.travelLocationShort,
			title: vm.title,
			office: vm.office,
			isDeliverable: vm.isDeliverable,
			deliverableSoftware: vm.deliverableSoftware,
			deliverableFileFormat: vm.deliverableFileFormat,
			deliverableCodeStandard: vm.deliverableCodeStandard,
			projectManager: vm.projectManager,
		};
	}
}

interface CrewTypeFM {
	id: number;
	name: string;
	color: ColorPalette;
	foremanEnabled: boolean;
}

interface WorkOrderReportTypeLookupFM {
	id: number;
	reportTypeId: number;
	name?: string;
	index: Nullable<number>;
	isRequired: boolean;
}

interface WorkOrderReportTypeLookupArrayFM {
	reportTypeLookup: WorkOrderReportTypeLookupFM;
	status: FormFieldArrayStatus;
}

interface UserInfoFM {
	accountId?: Nullable<number>;
	userId?: number;
	firstName?: string;
	lastName?: string;
	fullName: string;
}

export class AttachmentFM {
	id: number;
	size: number;
	name: string;
	type: FileType;
	status: ResourceStatus;
	storageName: string;
	storageContainer: string;
	/** original version with presigned url */
	originalSrc: string;
	lastModifiedAt: Nullable<Date>;
	uploadedBy: Nullable<UserInfoFM>;
	/** Tells whether it is copied from Job*/
	isJobCopy: boolean;

	public static fromViewModel(attachment: W_Job_FindDataForWorkOrderForm_VM_Attachment): AttachmentFM {
		return {
			id: attachment.id,
			size: attachment.size,
			name: attachment.name,
			type: attachment.type,
			status: attachment.status,
			storageName: attachment.storageName,
			storageContainer: attachment.storageContainer,
			/** original version with presigned url */
			originalSrc: attachment.originalSrc,
			lastModifiedAt: null,
			uploadedBy: null,
			isJobCopy: true,
		};
	}

	public static bulkFromViewModel(attachments: W_Job_FindDataForWorkOrderForm_VM_Attachment[]): AttachmentFM[] {
		return attachments.map(AttachmentFM.fromViewModel);
	}
}

export class WorkOrderDirectoryFM {
	id: number;
	name: string;
	parentId: Nullable<number>;
	icon: Nullable<DirectoryIcon>;
	lastModifiedAt: Nullable<Date>;
	lastModifiedBy: Nullable<UserInfoFM>;
	attachmentIds: number[];
	/** Tells whether it is copied from Job*/
	isJobCopy: boolean;

	public static fromViewModel(directory: W_Job_FindDataForWorkOrderForm_VM_Directory): WorkOrderDirectoryFM {
		return {
			id: directory.id,
			name: directory.name,
			parentId: directory.parentId,
			icon: directory.icon,
			lastModifiedAt: null,
			lastModifiedBy: null,
			attachmentIds: (directory.attachments ?? []).map(getId),
			isJobCopy: true,
		};
	}

	public static bulkFromViewModel(directories: W_Job_FindDataForWorkOrderForm_VM_Directory[]): WorkOrderDirectoryFM[] {
		return directories.map(WorkOrderDirectoryFM.fromViewModel);
	}
}

interface ProjectManagerFM {
	id: number;
	firstName: string;
	lastName: string;
	fullName: string;
	uniqueId: string;
}

export class JobChangedFM {
	id: number;
	status: WorkRequestStatus;
	isInternal: boolean;
	customerCompany: Nullable<string>;
	customerFormatted: Nullable<string>;
	customerFullName: Nullable<string>;
	office: Nullable<string>;
	title: Nullable<string>;
	projectManagerId?: Nullable<number>;
	projectManager?: Nullable<ProjectManagerFM>;
	jobCode: string;
	travelLocationShort: Nullable<string>;
	deliverableSoftware: Nullable<string>;
	deliverableFileFormat: Nullable<string>;
	deliverableCodeStandard: Nullable<string>;
	isDeliverable: boolean;

	constructor(newJob: JobUpsertVM) {
		this.id = newJob.id;
		this.status = newJob.status;
		this.isInternal = newJob.isInternal;
		this.customerCompany = newJob.customerCompanyName;
		this.customerFormatted = newJob.customerContact?.fullName ? `${newJob.customerContact.fullName}${this.customerCompany ? `, ${this.customerCompany}` : ''}` : null;
		this.customerFullName = newJob.customerContact?.fullName ?? null;
		this.office = newJob.office?.nickname ?? null;
		this.title = newJob.title;
		this.projectManagerId = newJob.projectManagerId;
		this.projectManager = newJob.projectManager;
		this.jobCode = newJob.jobCode;
		this.travelLocationShort = newJob.travelLocationShort;
		this.deliverableSoftware = newJob.deliverableSoftware?.name ?? null;
		this.deliverableFileFormat = newJob.deliverableFileFormat?.name ?? null;
		this.deliverableCodeStandard = newJob.deliverableCodeStandard?.name ?? null;
		this.isDeliverable = newJob.isDeliverable;
	}

}

class WorkOrderFM {
	id: number;
	code: string;
	dailyCode: Nullable<number>;
	index: Nullable<number>;
	/** YYYY-MM-DD */
	dueDate: string;
	cancellationReason: Nullable<string>;
	status: WorkOrderStatus;

	jobId: number;
	job: JobFM;
	jobTimezone: Nullable<string>;
	crewTypeId: Nullable<number>;
	crewType: Nullable<CrewTypeFM>;
	customCrewType: Nullable<string>;
	crewNumber: Nullable<string>;
	timeToStart: Nullable<number>;
	timeToEnd: Nullable<number>;
	shift: ShiftFM;
	isInternal: boolean;

	notes: Nullable<string>;
	scopeOfWork: Nullable<string>;

	projectManagerId: Nullable<number>;
	projectManager: Nullable<UserFM>;
	supervisorId: number;
	supervisor: UserFM;
	siteContact: Nullable<ContactFM>;
	workLocationAddress: Nullable<string>;
	officeNickname: Nullable<string>;
	divisionName: Nullable<string>;
	customerName: Nullable<string>;

	addresses: AddressArrayFM[];

	workOrderResourceLookups: WorkOrderResourceLookupFM[];

	workOrderReportTypeLookup: WorkOrderReportTypeLookupArrayFM[];
	updateExistingReports: boolean;

	position: WorkOrderPosition;

	excludeFromNotify: boolean;
	excludeTempLaborFromNotify: boolean;
	excludeFromDeliverables: boolean;

	revenue: Nullable<string>;
	manHourAverage: Nullable<string>;
	jobHours: Nullable<number>;
	shopHours: Nullable<number>;
	travelHours: Nullable<number>;
	workDescription: Nullable<string>;

	deliverableSoftware: Nullable<string>;
	deliverableCodeStandard: Nullable<string>;
	deliverableFileFormat: Nullable<string>;

	updatedAt: Date;
	updatedBy: UpdatedByFM;

	customerWorkOrder: Nullable<string>;

	directories: WorkOrderDirectoryFM[];
	uploadedAttachmentIds: number[];

	attachments: AttachmentFM[];
	uploadingAttachments: Record<string, true>;

	constructor(vm: WorkOrderUpsertVM) {
		this.id = vm.id;
		this.code = vm.code;
		this.dailyCode = vm.dailyCode;
		this.index = vm.index;
		this.dueDate = TimeUtils.formatDate(vm.dueDate, TimeFormat.DB_DATE_ONLY, TimeFormat.DATE_ONLY);
		this.cancellationReason = vm.cancellationReason;
		this.status = vm.status;
		this.jobId = vm.jobId;
		this.job = vm.job;
		this.jobTimezone = vm.jobTimezone;
		this.isInternal = vm.isInternal;
		this.crewTypeId = vm.crewTypeId;
		this.crewType = vm.crewType;
		this.customCrewType = vm.customCrewType;
		this.crewNumber = vm.crewNumber;
		this.timeToStart = TimeOptionUtils.toNumber(vm.timeToStart);
		this.timeToEnd = TimeOptionUtils.toNumber(vm.timeToEnd);
		this.shift = vm.shift;
		this.notes = vm.notes;
		this.scopeOfWork = vm.scopeOfWork;
		this.projectManagerId = vm.projectManagerId;
		this.projectManager = vm.projectManager;
		this.supervisorId = vm.superintendentId;
		this.supervisor = vm.superintendent;
		this.siteContact = vm.siteContact;
		this.workLocationAddress = vm.workLocationAddress;
		this.officeNickname = vm.officeNickname;
		this.divisionName = vm.divisionName;
		this.customerName = vm.customerName;
		this.addresses = AddressArrayFM.bulkConstructor(vm.addresses);
		this.workOrderResourceLookups = vm.workOrderResourceLookups;
		this.workOrderReportTypeLookup = vm.workOrderReportTypeLookups.map((reportTypeLookup) => ({
			reportTypeLookup,
			status: FormFieldArrayStatus.OLD,
		}));
		this.updateExistingReports = false;
		this.position = WorkOrderPosition.DEFAULT;
		this.excludeFromNotify = vm.excludeFromNotify;
		this.excludeTempLaborFromNotify = vm.excludeTempLaborFromNotify,
			this.excludeFromDeliverables = vm.excludeFromDeliverables;
		this.revenue = vm.revenue;
		this.manHourAverage = vm.manHourAverage;
		this.jobHours = vm.jobHours;
		this.shopHours = vm.shopHours;
		this.travelHours = vm.travelHours;
		this.workDescription = vm.workDescription;
		this.deliverableSoftware = vm.deliverableSoftware;
		this.deliverableCodeStandard = vm.deliverableCodeStandard;
		this.deliverableFileFormat = vm.deliverableFileFormat;
		this.updatedAt = vm.updatedAt;
		this.updatedBy = vm.updatedBy;
		this.customerWorkOrder = vm.customerWorkOrder;
		this.directories = vm.directories.map((_dir) => ({ ..._dir, isJobCopy: false }));
		this.uploadedAttachmentIds = [];
		this.attachments = vm.attachments.map((_att) => ({ ..._att, isJobCopy: false }));

		this.uploadingAttachments = {};
	}

	static toRequestModel(form: WorkOrderFM): WorkOrderRM {
		return {
			id: form.id,
			code: form.code,
			dailyCode: form.dailyCode,
			index: form.index,
			dueDate: form.dueDate,
			cancellationReason: form.cancellationReason,
			status: form.status,
			jobId: form.jobId,
			job: form.job,
			crewTypeId: form.crewTypeId,
			crewType: form.crewType,
			customCrewType: form.customCrewType,
			crewNumber: form.crewNumber ?? null,
			timeToStart: form.timeToStart,
			timeToEnd: form.timeToEnd,
			shift: form.shift,
			isInternal: form.isInternal,
			notes: form.notes,
			scopeOfWork: form.scopeOfWork,
			projectManagerId: form.projectManagerId,
			projectManager: form.projectManager,
			supervisorId: form.supervisorId,
			supervisor: form.supervisor,
			siteContact: form.siteContact,
			workLocationAddress: form.workLocationAddress,
			officeNickname: form.officeNickname,
			divisionName: form.divisionName,
			customerName: form.customerName,
			addresses: AddressArrayFM.bulkToRequestModel(form.addresses),
			workOrderResourceLookups: form.workOrderResourceLookups?.length
				? WorkOrderResourceLookupFM.bulkToRequestModel(form.workOrderResourceLookups)
				: [],
			workOrderReportTypeLookup: form.workOrderReportTypeLookup,
			updateExistingReports: form.updateExistingReports,
			position: form.position,
			excludeFromNotify: form.excludeFromNotify,
			excludeTempLaborFromNotify: form.excludeTempLaborFromNotify,
			excludeFromDeliverables: form.excludeFromDeliverables,
			revenue: form.revenue,
			manHourAverage: form.manHourAverage,
			jobHours: form.jobHours,
			shopHours: form.shopHours,
			travelHours: form.travelHours,
			workDescription: form.workDescription,
			deliverableSoftware: form.deliverableSoftware,
			deliverableCodeStandard: form.deliverableCodeStandard,
			deliverableFileFormat: form.deliverableFileFormat,
			updatedAt: form.updatedAt,
			updatedBy: form.updatedBy,
			customerWorkOrder: form.customerWorkOrder,
			directories: form.directories,
			uploadedAttachmentIds: form.uploadedAttachmentIds,
		};
	}

}

export default WorkOrderFM;
